import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, tap } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { ProductType } from "../models/product-type.model";
import { ApiService } from "./api.service";

@Injectable({ providedIn: "root" })
export class ProductTypeService {
  private readonly baseUrl = environment.apiUrl;
  private readonly cache$ = new BehaviorSubject<ProductType[] | null>(null);
  private loadInFlight: Observable<ProductType[]> | null = null;

  constructor(private api: ApiService) {}

  getProductTypes(): Observable<ProductType[]> {
    const cached = this.cache$.value;
    if (cached) {
      return of(cached);
    }

    if (this.loadInFlight) {
      return this.loadInFlight;
    }

    this.loadInFlight = this.api
      .authenticatedGet<ProductType[] | { product_types?: ProductType[]; records?: ProductType[] }>(
        `${this.baseUrl}/product-types`,
      )
      .pipe(
        map((res) => this.normalizeList(res)),
        tap((list) => {
          this.cache$.next(list);
          this.loadInFlight = null;
        }),
        catchError((err) => {
          this.loadInFlight = null;
          throw err;
        }),
      );

    return this.loadInFlight;
  }

  getProductionRequestAudioProductTypes(audioId: number): Observable<ProductType[]> {
    return this.api
      .authenticatedGet<ProductType[] | { product_types?: ProductType[]; records?: ProductType[] }>(
        `${this.baseUrl}/production-request-audio/${audioId}/product-types`,
      )
      .pipe(map((res) => this.normalizeList(res)));
  }

  getRecordingProductTypes(recordingId: number): Observable<ProductType[]> {
    return this.api
      .authenticatedGet<unknown>(`${this.baseUrl}/recordings/${recordingId}/product-types`)
      .pipe(
        switchMap((res) => this.resolveProductTypes(res)),
        catchError(() => of([] as ProductType[])),
      );
  }

  /** Map API payloads to ProductType[], resolving id-only responses via catalog. */
  private resolveProductTypes(res: unknown): Observable<ProductType[]> {
    const list = this.normalizeList(res);
    if (list.length > 0) {
      return of(list);
    }

    const ids = this.extractProductTypeIds(res);
    if (!ids.length) {
      return of([]);
    }

    return this.getProductTypes().pipe(
      map((catalog) => catalog.filter((pt) => ids.includes(pt.id))),
    );
  }

  private extractProductTypeIds(res: unknown): number[] {
    if (Array.isArray(res) && res.every((item) => typeof item === "number")) {
      return res as number[];
    }
    if (!res || typeof res !== "object") {
      return [];
    }
    const body = res as Record<string, unknown>;
    const raw = body["product_type_ids"] ?? body["productTypeIds"] ?? body["ids"];
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.filter((id): id is number => typeof id === "number");
  }

  private normalizeList(res: unknown): ProductType[] {
    if (!res) return [];
    if (Array.isArray(res)) {
      if (!res.length || typeof res[0] === "number") {
        return [];
      }
      return res.filter(
        (item): item is ProductType =>
          !!item &&
          typeof item === "object" &&
          typeof (item as ProductType).name === "string",
      );
    }
    if (typeof res !== "object") {
      return [];
    }
    const body = res as Record<string, unknown>;
    const keys = ["product_types", "productTypes", "records", "data"] as const;
    for (const key of keys) {
      const value = body[key];
      if (Array.isArray(value)) {
        return value.filter(
          (item): item is ProductType =>
            !!item &&
            typeof item === "object" &&
            typeof (item as ProductType).name === "string",
        );
      }
    }
    return [];
  }
}
